import comp from '../components/barrel';
import svg from '../js/svg';
import config from '../js/config';
import O from '../js/core';
import U from '../js/utils';

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const FINALIZED_STATUS_UUID = 'd85f2775-4c3a-4ad7-9f9e-a41767f95aae';
const SIGNED_WILL_UPLOAD_STATUS_UUID = '6b56a24c-de0d-4f39-a103-7110d5a90413';
const DRAFT_UPLOADED_STATUS_UUID = 'cd9353c9-d692-4751-b6f3-0a4352653284';

/** API returns ISO strings like 2026-02-18T00:00:00.000Z — U.formatDate splits on '-' only and is unreliable for full ISO. */
function formatIsoDateUtc(iso) {
  if (iso == null || iso === '') return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getUTCDate()} ${MONTHS_SHORT[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

export default {
  applicationStatusUuid: null,
  isViewOnly() {
    return this.recipe?.page === 'VAt_Will_advisor';
  },
  isFinalisedStatus() {
    return this.applicationStatusUuid === FINALIZED_STATUS_UUID;
  },
  isSignedWillUploadStatus() {
    return this.applicationStatusUuid === SIGNED_WILL_UPLOAD_STATUS_UUID;
  },
  isDraftUploadedStatus() {
    return this.applicationStatusUuid === DRAFT_UPLOADED_STATUS_UUID || this.appStatus === 'Draft uploaded';
  },
  async prepare() {
    this.application_uuid = thsPage.vars.application_uuid;
    this.recipe.methods.push(await O.mPOST('communal/languages'));

    const vars = {};
    vars.application_uuid = this.application_uuid;

    this.recipe.methods.push(await O.mPOST('wills/get-app-will-details', vars));
    this.recipe.methods.push(await O.mPOST('application/get-application-detail', vars));
    this.recipe.methods.push(await O.mPOST('wills/get-app-signed-wills', vars));
    this.recipe.methods.push(await O.mPOST('application/get-app-status', vars));
  },
  async render() {
    const rec = this.recipe.methods;

    let data = {};
    if (rec.length) {
      data = await O.PREP(rec);
    }
    await this.loadPage(data);
  },
  async loadPage(data) {
    const dataAppDets = data['wills/get-app-will-details']?.data || [];
    const {pages} = data['application/get-application-detail']?.data || {};

    const statusData = data['application/get-app-status']?.data || {};
    this.applicationStatusUuid = statusData.application_status_uuid || null;
    if (statusData.short_text) {
      this.appStatus = statusData.short_text;
    }
    if (!this.applicationStatusUuid) {
      await this.updateStatus();
    }

    // Build Progress Steps
    const progressDiv = U.id('progressDiv');
    progressDiv.innerHTML = '';

    let progressSteps = await comp.progressSteps(4);
    progressDiv.prepend(progressSteps);

    const filteredItems = (Array.isArray(dataAppDets) ? dataAppDets : []).filter((item) => item.active_yn !== false);
    // API may return current_step as string (e.g. "4"); switch/case uses strict equality — must coerce to number
    const currentStep = filteredItems.length ? Number(filteredItems[0].current_step) : NaN;
    const atDraftStep = [2, 3, 4].includes(currentStep);

    // Imported / finalised / signed-will-upload apps often have no will-details row and incomplete AD pages.
    // Do not send those to step 0 — they still need signed-will upload + dates.
    if (this.isFinalisedStatus()) {
      await this.goToStep3(data);
      return;
    }

    // Draft uploaded with no will-details row has no current_step. Still show Request Redraft.
    if (this.isDraftUploadedStatus() && !filteredItems.length) {
      await this.goToStep3(data);
      return;
    }

    // Only block on incomplete AD pages if draft upload flow hasn't started yet
    if (!atDraftStep && pages && !this.isSignedWillUploadStatus()) {
      for (let page in pages) {
        if (pages[page].complete !== true) {
          await this.goToStep0();
          return;
        }
      }
    }

    const swBlock = data['wills/get-app-signed-wills'];
    const signedWills = Array.isArray(swBlock?.data) ? swBlock.data : [];
    const hasSignedWills = signedWills.length > 0;
    const useSignedOnly = hasSignedWills && !atDraftStep;

    if (useSignedOnly) {
      await this.goToSignedOnly(data);
      return;
    }

    if (filteredItems.length) {
      // Go immediately to step number
      switch (currentStep) {
        case 2:
          await this.goToStep2();
          break;
        case 3:
          await this.goToStep3(data);
          break;
        case 4:
          await this.goToStep4(data);
          break;
        default:
          await this.goToStep1(data);
      }
    } else {
      await this.goToStep1(data);
    }
  },
  async goToSignedOnly(prefetchedData) {
    this.changeSection('stepSignedOnly');
    const signedWill = await this.resolveSignedWillsData(prefetchedData);
    this.buildSignedWillTable(signedWill, 'signedWillTableOnly');
  },
  async goToStep0() {
    U.updateProgressSteps(0);
    this.changeSection('step0');
  },
  async goToStep1(data) {
    const dataLangDD = data['communal/languages'].data;

    // Language DropDown
    const langDD = await comp.select('language_id', dataLangDD, 'language_uuid', 'long_text', 'Will language', 1);
    U.id('langSelect').innerHTML = '';
    U.id('langSelect').appendChild(langDD);
    this.changeSection('step1');
  },
  async goToStep2() {
    await this.changeSection('step2');
    U.updateProgressSteps(2);
    await this.step1Submit2();
  },
  async goToStep3(prefetchedData) {
    this.changeSection('step3');
    U.updateProgressSteps(3);
    const draftList = await this.getDraftWills();
    const signedWill = await this.resolveSignedWillsData(prefetchedData);
    this.buildDraftWillTable(Array.isArray(draftList) ? draftList : [], 'draftWillTableDiv2');
    this.buildSignedWillTable(signedWill, 'signedWillTableDiv');
  },
  async goToStep4(prefetchedData) {
    this.changeSection('step4');
    U.updateProgressSteps(4);
    const draftList = await this.getDraftWills();
    const signedWill = await this.resolveSignedWillsData(prefetchedData);
    this.buildDraftWillTable(draftList, 'draftWillTableDiv3');
    this.buildSignedWillTable(signedWill, 'signedWillTableDiv2');
  },
  async resolveSignedWillsData(prefetchedData) {
    const fromPrep = prefetchedData?.['wills/get-app-signed-wills']?.data;
    if (Array.isArray(fromPrep)) {
      return fromPrep;
    }
    return this.getSignedWill();
  },
  async updateStatus() {
    const vars = {};
    vars.application_uuid = this.application_uuid;

    await O.PREP([await O.mPOST('application/get-app-status', vars)])
      .then(async (res) => {
        const statusData = res['application/get-app-status'].data || {};
        const {short_text, application_status_uuid} = statusData;
        this.appStatus = short_text;
        this.applicationStatusUuid = application_status_uuid || null;

        const status_row = U.id('status_row');
        status_row.innerHTML = '';
        let statusText = await comp.statusTag(short_text);
        status_row.appendChild(statusText);
      })
      .catch(() => {});
  },
  async changeSection(activeSection) {
    await this.updateStatus();
    if (activeSection === 'stepSignedOnly') {
      U.hide('progressDiv');
    } else {
      U.unhide('progressDiv');
    }
    const sections = document.querySelectorAll('.stepSection');
    sections.forEach((sect) => {
      sect.id && U.hide(sect.id);
    });
    U.unhide(activeSection);
  },
  async submitForDrafting() {
    let isValid = await U.validateForm('step1');
    if (!isValid) {
      return;
    }

    const popup = await this.submitForDraftingModal();
    document.body.prepend(popup);
  },
  async submitForDraftingModal() {
    const checklistIcon = `
      <span class="flex-shrink-0 w-5 h-5 rounded-full bg-primary flex items-center justify-center mt-0.5" aria-hidden="true">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" class="w-3 h-3" fill="none">
          <path d="M3.5 8.25 6.5 11.25 12.5 5.25" stroke="#fff" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>`;
    const checklistItems = [
      'Client information reviewed',
      'Will drafting instructions completed',
      'Supporting documentation (if applicable)',
      'Special instructions or advisor notes included',
    ];
    const checklistHtml = checklistItems
      .map(
        (item) => `
          <li class="flex items-start gap-2.5 text-sm text-gray-700">
            ${checklistIcon}
            <span>${item}</span>
          </li>`
      )
      .join('');

    const div = `
      <div id="submitForDraftingModal" class="fixed inset-0 z-50 overflow-y-auto bg-opacity-50 bg-black">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <div class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-xl">
            <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
              <div class="flex items-start gap-4 mb-4">
                <div class="relative flex-shrink-0 w-12 h-12 rounded-full bg-primary-opacity flex items-center justify-center" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-6 h-6 text-primary" fill="none">
                    <path d="M8 2h8l4 4v14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
                    <path d="M16 2v4h4" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
                    <path d="M8 10h5M8 13h4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                  <span class="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" class="w-3 h-3" fill="none">
                      <path d="M3.5 8.25 6.5 11.25 12.5 5.25" stroke="#fff" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </span>
                </div>
                <h4 class="text-primary-dark font-bold text-lg leading-snug m-0 pt-1">Confirm Will Drafting Submission</h4>
              </div>

              <p class="text-sm text-gray-700 mb-1">You are about to submit this will application to Legatus Trust for drafting.</p>
              <p class="text-sm text-gray-700 mb-4">Please confirm that the following is correct/completed:</p>

              <ul class="space-y-2.5 mb-5">
                ${checklistHtml}
              </ul>

              <hr class="border-gray-200 mb-4" />

              <p class="text-sm text-gray-700 mb-3">Once submitted, this application will be sent to the Legatus Trust Will drafters and the status will change to <strong>Draft requested</strong>.</p>
              <p class="text-sm text-gray-700 mb-0">If you would like to change any of the information after submitting the application, you can request a redraft from the Will Draft tab.</p>
            </div>

            <div class="bg-gray-50 px-4 py-3 flex md:justify-end justify-center sm:px-6 gap-3">
              <button type="button" class="btn-outline" onclick="U.closeModal('submitForDraftingModal')">Review Application</button>
              <button type="button" class="btn" onclick="U.closeModal('submitForDraftingModal'), thsTab.step1Submit()">Submit for Drafting</button>
            </div>
          </div>
        </div>
      </div>
    `;

    return await U.createHTMLElementFromString(div);
  },
  async step1Submit() {
    const vars = {};
    vars.language_uuid = U.id('language_id').value;
    vars.application_uuid = this.application_uuid;
    vars.special_instructions = U.id('specialInstrucBox').value;

    O.PREP([await O.mPOST('wills/req-new-draft', vars)])
      .then(async () => {
        this.goToStep2();
      })
      .catch(() => {});
  },
  async step1Submit2() {
    const draftList = await this.getDraftWills();
    this.buildDraftWillTable(draftList, 'draftWillTableDiv1');
  },
  async getDraftWills() {
    const vars = {};
    vars.application_uuid = this.application_uuid;

    const data = await O.PREP([await O.mPOST('wills/get-app-draft-wills', vars)])
      .then(async (res) => {
        return res['wills/get-app-draft-wills'].data;
      })
      .catch(() => []);

    return Array.isArray(data) ? data : [];
  },
  async getSignedWill() {
    const vars = {};
    vars.application_uuid = this.application_uuid;

    const data = await O.PREP([await O.mPOST('wills/get-app-signed-wills', vars)])
      .then(async (res) => {
        return res['wills/get-app-signed-wills'].data;
      })
      .catch(() => []);

    return Array.isArray(data) ? data : [];
  },
  async buildDraftWillTable(data, tableDiv) {
    const table = U.id(tableDiv);
    table.innerHTML = '';

    const showRemoveColumn = !this.isViewOnly() && data && data.some((row) => row.remove_yn === true);

    // HEADER
    const thead = U.divAtt('thead', 'class', 'w-full');
    const headRow = thead.insertRow();

    const h1 = headRow.insertCell(0);
    h1.innerHTML = 'Date';
    const h2 = headRow.insertCell(1);
    h2.innerHTML = 'Recorded By';
    const h3 = headRow.insertCell(2);
    h3.innerHTML = 'File Name';
    const h4 = headRow.insertCell(3);
    h4.innerHTML = 'File Type';
    const h5 = headRow.insertCell(4);
    h5.innerHTML = 'Reference';
    const h6 = headRow.insertCell(5);
    h6.innerHTML = '';
    if (showRemoveColumn) {
      const h7 = headRow.insertCell(6);
      h7.innerHTML = '';
    }
    thead.appendChild(headRow);
    table.appendChild(thead);

    // Body
    const tbody = U.divAtt('tbody');
    table.appendChild(tbody);

    if (data.length) {
      data.forEach(async (row) => {
        const willRow = tbody.insertRow();
        const r1 = willRow.insertCell(0);
        r1.innerHTML = await U.formatDate(row.created_date);
        const r2 = willRow.insertCell(1);
        r2.innerHTML = `${row.f_name} ${row.s_name}`;
        const r3 = willRow.insertCell(2);
        r3.innerHTML = row.file_name;
        const r4 = willRow.insertCell(3);
        r4.innerHTML = row.file_type;
        const r5 = willRow.insertCell(4);
        r5.innerHTML = row.file_reference;
        const downloadCell = willRow.insertCell(5);
        const download_onclick = `thsTab.downloadFile('${row.file_uuid}')`;
        const linkSVG = svg.download(download_onclick);
        downloadCell.appendChild(linkSVG);
        if (showRemoveColumn) {
          const removeCell = willRow.insertCell(6);
          if (row.remove_yn === true) {
            const fileTypeEsc = (row.file_type || '').replace(/'/g, "\\'");
            const fileRefEsc = (row.file_reference || '').replace(/'/g, "\\'");
            const replace_onclick = `thsTab.replaceFileModal('${row.file_uuid}', '${fileTypeEsc}', '${fileRefEsc}')`;
            const removeSVG = svg.replaceRecycle(replace_onclick);
            removeCell.appendChild(removeSVG);
          }
        }
      });
    }

    if (tableDiv === 'draftWillTableDiv1' && !this.isViewOnly()) {
      const uploadRow = U.id('uploadRowDraft');
      uploadRow.innerHTML = '';

      if (this.appStatus === 'Draft requested - revision') {
        const msg = U.divAtt('p');
        msg.classList.add('text-sm', 'text-gray-700', 'py-3', 'px-3', 'text-center');
        msg.textContent =
          'The will details have a revision on record since requesting the draft. Generate a new summary document before you can upload a draft';
        uploadRow.appendChild(msg);
      } else {
        const uploadDiv = U.divAtt('div');
        uploadDiv.classList.add('relative', 'w-full', 'flex', 'justify-center', 'items-center', 'transition-colors', 'duration-200', 'py-3', 'px-3', 'cursor-pointer', 'bg-primary-opacity', 'hover:bg-secondary', 'border-none');

        uploadDiv.setAttribute('role', 'button');
        uploadDiv.setAttribute('aria-label', 'Upload file');

        uploadDiv.onclick = () => thsTab.uploadFileModal();

        const textcontainer = U.divAtt('span');
        textcontainer.classList.add('ml-2');
        textcontainer.innerHTML = 'Upload Draft';

        const linkSVG = svg.uploadCloud();
        uploadDiv.appendChild(linkSVG);
        uploadDiv.appendChild(textcontainer);

        uploadDiv.style.margin = '0';
        uploadDiv.style.width = '100%';
        uploadDiv.style.boxSizing = 'border-box';

        uploadRow.appendChild(uploadDiv);
      }
    }
  },
  async downloadFile(file_uuid) {
    const vars = {};
    vars.file_uuid = file_uuid;

    O.PREP([await O.mPOST('files/get-file-url', vars)])
      .then(async (res) => {
        window.open(res['files/get-file-url'].data.signedUrl, '_blank');
      })
      .catch(() => {});
  },
  async replaceFileModal(file_uuid, file_type, file_reference) {
    this.replaceFileUuid = file_uuid;
    this.replaceFileType = file_type || '';
    this.replaceFileReference = file_reference || '';
    const popup = await this.replaceDraftModal();
    document.body.prepend(popup);

    const fileInput = U.id('replaceFileUpload');
    const replaceBtn = U.id('replace-modal-btn');
    if (fileInput) {
      fileInput.onchange = () => {
        const file = fileInput.files[0];
        fileInput.classList.remove('input-error');
        const fbEl = document.querySelector('[fb-for="replaceFileUpload"]');
        if (fbEl) fbEl.textContent = '';
        const isPdf = this.replaceFileType === 'application/pdf';
        const validPdf = file && file.type === 'application/pdf';
        const validWord = file && (file.type === 'application/msword' || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document');
        const isValid = isPdf ? validPdf : validWord;
        if (file && !isValid) {
          fileInput.classList.add('input-error');
          if (fbEl) fbEl.textContent = `Please select a ${isPdf ? 'PDF' : 'Word'} document`;
        }
        if (replaceBtn) replaceBtn.disabled = !!(file && !isValid);
      };
    }
  },
  async replaceDraftModal() {
    const isPdf = this.replaceFileType === 'application/pdf';
    const accept = isPdf
      ? 'application/pdf'
      : '.doc, .docx, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    const labelName = isPdf ? 'PDF Document:' : 'Word Document:';

    const fileInput = await comp.inputFile({
      elID: 'replaceFileUpload',
      labelName,
      accept,
      requiredYN: true,
    });
    const referenceDiv = await comp.inputText('replaceReferenceDiv', this.replaceFileReference, 'Reference:', true);

    const div = `
    <div id="replaceModal" class="fixed inset-0 z-50 overflow-y-auto bg-opacity-50 bg-black">
      <div id="replaceDocumentDiv" class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
          <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            <div class="text-center">
              <h2 class="text-2xl font-semibold mb-4">Replace Draft</h2>
              <p class="text-gray-500 mb-4">Select a ${isPdf ? 'PDF' : 'Word'} file to replace the existing document.</p>
            </div>
            <div class="text-left space-y-4">
              ${fileInput.outerHTML}
              ${referenceDiv.outerHTML}
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 flex md:justify-end justify-center sm:px-6">
            <button class="btn-outline mr-3" onclick="U.closeModal('replaceModal')">Cancel</button>
            <button id="replace-modal-btn" class="btn-primary" onclick="thsTab.replaceFile()">Replace Document</button>
          </div>
        </div>
      </div>
    </div>
    `;

    return await U.createHTMLElementFromString(div);
  },
  async replaceFile() {
    let isValid = await U.validateForm('replaceDocumentDiv');
    if (!isValid) return;

    const file = U.id('replaceFileUpload').files[0];
    const referenceVal = U.id('replaceReferenceDiv').value;
    const isPdf = this.replaceFileType === 'application/pdf';

    U.id('replaceFileUpload').classList.remove('input-error');
    U.id('replaceReferenceDiv').classList.remove('input-error');
    const fbEl = document.querySelector('[fb-for="replaceFileUpload"]');
    if (fbEl) fbEl.textContent = '';

    if (!file) {
      U.id('replaceFileUpload').classList.add('input-error');
      if (fbEl) fbEl.textContent = 'Please select a document';
      return;
    }
    if (isPdf && file.type !== 'application/pdf') {
      U.id('replaceFileUpload').classList.add('input-error');
      if (fbEl) fbEl.textContent = 'Please select a PDF document';
      return;
    }
    if (!isPdf && file.type !== 'application/msword' && file.type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      U.id('replaceFileUpload').classList.add('input-error');
      if (fbEl) fbEl.textContent = 'Please select a Word document';
      return;
    }
    if (!referenceVal) {
      U.id('replaceReferenceDiv').classList.add('input-error');
      return;
    }

    const formData = new FormData();
    formData.append('application_uuid', this.application_uuid);
    formData.append('file_uuid', this.replaceFileUuid);
    formData.append('file_reference', referenceVal);

    if (isPdf) {
      formData.append('pdfUpload', file);
    } else {
      formData.append('wordUpload', file);
    }

    const hdrs = await O.getAuthHdrs();

    U.loaderBtn(1, 'replace-modal-btn');

    try {
      const response = await fetch(`${config.apiURL}wills/upl-draft-will-files`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${hdrs.token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          const errorText = await response.text();
          errorData = { status: 'FAILED', data: { error: errorText || 'File replace failed' } };
        }
        throw { status: response.status, data: errorData };
      }

      U.loaderBtn(0, 'replace-modal-btn');
      U.closeModal('replaceModal');
      await this.render();
    } catch (error) {
      U.loaderBtn(0, 'replace-modal-btn');
      if (error.status && error.data) {
        O.handleFetchError(error);
      } else {
        comp.errorBox('Error', error.message || 'Failed to replace file. Please try again.');
      }
    }
  },
  async replaceSignedWillModal(file_uuid) {
    this.replaceSignedFileUuid = file_uuid;
    const popup = await this.replaceSignedWillDraftModal();
    document.body.prepend(popup);

    const fileInput = U.id('replaceSignedFileUpload');
    const replaceBtn = U.id('replace-signed-modal-btn');
    if (fileInput) {
      fileInput.onchange = () => {
        const file = fileInput.files[0];
        fileInput.classList.remove('input-error');
        const fbEl = document.querySelector('[fb-for="replaceSignedFileUpload"]');
        if (fbEl) fbEl.textContent = '';
        const isValid = file && file.type === 'application/pdf';
        if (file && !isValid) {
          fileInput.classList.add('input-error');
          if (fbEl) fbEl.textContent = 'Please select a PDF document';
        }
        if (replaceBtn) replaceBtn.disabled = !!(file && !isValid);
      };
    }
  },
  async replaceSignedWillDraftModal() {
    const fileInput = await comp.inputFile({
      elID: 'replaceSignedFileUpload',
      labelName: 'PDF Document:',
      accept: 'application/pdf',
      requiredYN: true,
    });

    const div = `
    <div id="replaceSignedModal" class="fixed inset-0 z-50 overflow-y-auto bg-opacity-50 bg-black">
      <div id="replaceSignedDocumentDiv" class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
          <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            <div class="text-center">
              <h2 class="text-2xl font-semibold mb-4">Replace Signed Will</h2>
              <p class="text-gray-500 mb-4">Select a PDF file to replace the existing signed will document.</p>
            </div>
            <div class="text-left">
              ${fileInput.outerHTML}
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 flex md:justify-end justify-center sm:px-6">
            <button class="btn-outline mr-3" onclick="U.closeModal('replaceSignedModal')">Cancel</button>
            <button id="replace-signed-modal-btn" class="btn-primary" onclick="thsTab.replaceSignedWillFile()">Replace signed will</button>
          </div>
        </div>
      </div>
    </div>
    `;

    return await U.createHTMLElementFromString(div);
  },
  async replaceSignedWillFile() {
    let isValid = await U.validateForm('replaceSignedDocumentDiv');
    if (!isValid) return;

    const file = U.id('replaceSignedFileUpload').files[0];
    U.id('replaceSignedFileUpload').classList.remove('input-error');
    const fbEl = document.querySelector('[fb-for="replaceSignedFileUpload"]');
    if (fbEl) fbEl.textContent = '';

    if (!file) {
      U.id('replaceSignedFileUpload').classList.add('input-error');
      if (fbEl) fbEl.textContent = 'Please select a document';
      return;
    }
    if (file.type !== 'application/pdf') {
      U.id('replaceSignedFileUpload').classList.add('input-error');
      if (fbEl) fbEl.textContent = 'Please select a PDF document';
      return;
    }

    const formData = new FormData();
    formData.append('uploadFile', file);
    formData.append('application_uuid', this.application_uuid);
    formData.append('file_uuid', this.replaceSignedFileUuid);

    const hdrs = await O.getAuthHdrs();

    U.loaderBtn(1, 'replace-signed-modal-btn');

    try {
      const response = await fetch(`${config.apiURL}wills/repl-signed-will`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${hdrs.token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          const errorText = await response.text();
          errorData = { status: 'FAILED', data: { error: errorText || 'File replace failed' } };
        }
        throw { status: response.status, data: errorData };
      }

      U.loaderBtn(0, 'replace-signed-modal-btn');
      U.closeModal('replaceSignedModal');
      await this.render();
    } catch (error) {
      U.loaderBtn(0, 'replace-signed-modal-btn');
      if (error.status && error.data) {
        O.handleFetchError(error);
      } else {
        comp.errorBox('Error', error.message || 'Failed to replace file. Please try again.');
      }
    }
  },
  async uploadFileModal() {
    if (this.appStatus === 'Draft requested - revision') {
      return;
    }
    const popup = await this.fileUploadModal();
    document.body.prepend(popup);
  },
  async uploadSignedWillModal() {
    const draftList = await this.getDraftWills();
    const draftWills = Array.isArray(draftList) ? draftList : [];
    const pdfDraftWills = draftWills.filter((item) => item.file_type === 'application/pdf');
    if (!pdfDraftWills.length) {
      pdfDraftWills.push({
        file_uuid: '00000000-0000-0000-0000-000000000000',
        file_reference: 'Draft not generated on system',
      });
    }
    const signedWills = await this.getSignedWill();
    const popup = await this.signedWillUploadModal(pdfDraftWills, signedWills);
    document.body.prepend(popup);
  },
  async uploadFile() {
    U.id('modal-btn').disabled = true;
    U.loaderBtn(1, 'modal-btn');
    U.id('pdfUpload').classList.remove('input-error');
    U.id('wordUpload').classList.remove('input-error');
    U.id('referenceDiv').classList.remove('input-error');

    // Collect the input values
    const pdfFile = U.id('pdfUpload').files[0];
    const wordFile = U.id('wordUpload').files[0];
    const referenceVal = U.id('referenceDiv').value;
    const application_uuid = this.application_uuid;

    let isValid = true;

    if (!pdfFile) {
      U.id('pdfUpload').classList.add('input-error');
      isValid = false;
    }

    if (pdfFile && pdfFile.type !== 'application/pdf') {
      U.id('pdfUpload').classList.add('input-error');
      isValid = false;
    }

    if (!wordFile) {
      U.id('wordUpload').classList.add('input-error');
      isValid = false;
    }

    if (wordFile && wordFile.type !== 'application/msword' && wordFile.type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      U.id('wordUpload').classList.add('input-error');
      isValid = false;
    }

    if (!referenceVal) {
      U.id('referenceDiv').classList.add('input-error');
      isValid = false;
    }

    if (!isValid) {
      U.loaderBtn(0, 'modal-btn');
      U.id('modal-btn').disabled = false;
      return;
    }
    // Create a FormData object to send the files and other fields
    const formData = new FormData();
    formData.append('pdfUpload', pdfFile);
    formData.append('wordUpload', wordFile);
    formData.append('file_reference', referenceVal);
    formData.append('application_uuid', application_uuid);

    const hdrs = await O.getAuthHdrs();

    // Send the form data using fetch
    try {
      const response = await fetch(`${config.apiURL}wills/upl-draft-will-files`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${hdrs.token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        // Try to parse error response as JSON, fallback to text
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          const errorText = await response.text();
          errorData = { status: 'FAILED', data: { error: errorText || 'File upload failed' } };
        }
        
        // Structure error for handleFetchError
        throw {
          status: response.status,
          data: errorData
        };
      }

      const result = await response.json();
      U.loaderBtn(0, 'modal-btn');
      U.closeModal('fileUploadModal');
      this.step2Submit();
    } catch (error) {
      U.loaderBtn(0, 'modal-btn');
      U.id('modal-btn').disabled = false;
      
      // Check if error is already structured for handleFetchError
      if (error.status && error.data) {
        O.handleFetchError(error);
      } else {
        // Fallback for other errors (network errors, etc.)
        const errorMsg = error.message || 'Failed to upload file. Please try again.';
        comp.errorBox('Error', errorMsg);
      }
    }
  },
  async fileUploadModal() {
    const pdfInputDiv = await comp.inputFile({elID: 'pdfUpload', labelName: 'PDF Document:', accept: 'application/pdf', requiredYN: true});
    const wordInputDiv = await comp.inputFile({elID: 'wordUpload', labelName: 'Word Document:', accept: '.doc, .docx, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document', requiredYN: true});
    const referenceDiv = await comp.inputText('referenceDiv', '', 'Reference:', true);
    const div = `
        <div id="fileUploadModal" class="fixed inset-0 z-50 overflow-y-auto bg-opacity-50 bg-black">
          <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <!-- Modal Content -->
              <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div class="text-center">
                  <!-- Heading -->
                  <h2 class="text-2xl font-semibold mb-4">Upload Draft</h2>
                  <!-- Subheading -->
                  <p class="text-gray-500 mb-6">Please upload both PDF and Word document.</p>
                </div>

                <!-- File Inputs -->
                <div class="space-y-4">
                  <!-- PDF Input -->
                  ${pdfInputDiv.outerHTML}

                  <!-- Word Document Input -->
                  ${wordInputDiv.outerHTML}

                  <!-- Reference Input -->
                  ${referenceDiv.outerHTML}

                </div>
              </div>

              <!-- Footer Buttons -->
              <div class="bg-gray-50 px-4 py-3 flex md:justify-end justify-center sm:px-6">
                <button class="btn-outline mr-3" onclick="U.closeModal('fileUploadModal')">Cancel</button>
                <button id="modal-btn" class="btn-primary" onclick="thsTab.uploadFile()">Upload Draft</button>
              </div>
            </div>
          </div>
        </div>
     `;

    return await U.createHTMLElementFromString(div);
  },
  async signedWillUploadModal(ddData, signedWills) {
    const singleSignedRow = signedWills && signedWills.length === 1 ? signedWills[0] : null;
    const lockExistingDates = Boolean(singleSignedRow) && !this.isFinalisedStatus();
    const toYYYYMMDD = (d) => {
      if (!d) return '';
      const s = typeof d === 'string' ? d : (d.toISOString && d.toISOString.call(d)) || '';
      return s.includes('T') ? s.slice(0, 10) : s;
    };

    let recDateHTML;
    let signDateHTML;
    let verificationDateHTML;

    if (lockExistingDates) {
      const receivedVal = toYYYYMMDD(singleSignedRow.received_date);
      const signedVal = toYYYYMMDD(singleSignedRow.signed_date);
      const verificationVal = toYYYYMMDD(singleSignedRow.verification_date);
      const receivedDisplay = receivedVal ? await U.formatDate(receivedVal) : '';
      const signedDisplay = signedVal ? await U.formatDate(signedVal) : '';
      const verificationDisplay = verificationVal ? await U.formatDate(verificationVal) : '';
      recDateHTML = `
        <div class="input-group">
          <label>Date Will Received:</label>
          <input type="hidden" id="dateReceived" value="${receivedVal || ''}" />
          <span class="block py-2 text-gray-700">${receivedDisplay || '—'}</span>
        </div>`;
      signDateHTML = `
        <div class="input-group">
          <label>Date Will Signed:</label>
          <input type="hidden" id="dateSigned" value="${signedVal || ''}" />
          <span class="block py-2 text-gray-700">${signedDisplay || '—'}</span>
        </div>`;
      verificationDateHTML = `
        <div class="input-group">
          <label>Verification date:</label>
          <input type="hidden" id="verificationDate" value="${verificationVal || ''}" />
          <span class="block py-2 text-gray-700">${verificationDisplay || '—'}</span>
        </div>`;
    } else {
      const receivedPrefill = singleSignedRow ? toYYYYMMDD(singleSignedRow.received_date) : null;
      const signedPrefill = singleSignedRow ? toYYYYMMDD(singleSignedRow.signed_date) : null;
      const verificationPrefill = singleSignedRow ? toYYYYMMDD(singleSignedRow.verification_date) : null;
      const [recDate, signDate, verificationDate] = await Promise.all([
        comp.inputDate('dateReceived', receivedPrefill, 'Date Will Received:', true),
        comp.inputDate('dateSigned', signedPrefill, 'Date Will Signed:', true),
        comp.inputDate('verificationDate', verificationPrefill, 'Verification date:', true),
      ]);
      recDateHTML = recDate.outerHTML;
      signDateHTML = signDate.outerHTML;
      verificationDateHTML = verificationDate.outerHTML;
    }

    const [fileInputDiv, filingNumber, textArea, select] = await Promise.all([
      comp.inputFile({elID: 'pdfUpload', labelName: 'PDF Document:', accept: 'application/pdf', requiredYN: true}),
      comp.inputText('filingNumber', '', 'Filing Number:', true),
      comp.textarea('signingNotes', '', 'Further Notes:', true),
      comp.select_v2({
        elID: 'docVersion',
        ddData,
        valueDataName: 'file_uuid',
        textDataName: 'file_reference',
        labelName: 'Document Version Number:',
        requiredYN: true,
      }),
    ]);

    const div2 = `
      <div id="fileUploadModal" class="fixed inset-0 z-50 overflow-y-auto bg-opacity-50 bg-black">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <div class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:mt-3 sm:mb-8 sm:ml-[50px] sm:w-full sm:max-w-4xl">
            <!-- Modal Content -->
            <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
              <div class="text-center">
                <!-- Heading -->
                <h2 class="text-2xl font-semibold mb-4">Upload Signed Will</h2>
              </div>
  
              <!-- Form Inputs -->
              <div class="space-y-4">
                <!-- Row 1: Date Will Received, Date Will Signed, Date Will Verified -->
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    ${recDateHTML}
                  </div>
                  <div>
                    ${signDateHTML}
                  </div>
                  <div>
                    ${verificationDateHTML}
                  </div>
                </div>

                <!-- Row 2: Document Version Number, Filing Number, PDF Document -->
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    ${select.outerHTML}
                  </div>
                  <div>
                    ${filingNumber.outerHTML}
                  </div>
                  <div>
                    ${fileInputDiv.outerHTML}
                  </div>
                </div>

                <!-- Row 3: Further Notes, Checkbox -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    ${textArea.outerHTML}
                  </div>
                  <div class="text-left flex items-center">
                    <label class="flex items-center w-full gap-2">
                      <input type="checkbox" class="form-checkbox text-primary w-1/6" id="confirmationCheckbox" required>
                      <span class="text-gray-700 w-5/6">I confirm the will has been signed correctly.</span>
                    </label>
                  </div>
                </div>

              </div>
            </div>
  
            <!-- Footer Buttons -->
            <div class="bg-gray-50 px-4 py-3 flex md:justify-end justify-center sm:px-6">
              <button class="btn-outline mr-3" onclick="U.closeModal('fileUploadModal')">Cancel</button>
              <button id="final-modal-btn" class="btn" onclick="thsTab.finaliseWill()">Finalise Will</button>
            </div>
          </div>
        </div>
      </div>
    `;

    return await U.createHTMLElementFromString(div2);
  },
  async step2Submit() {
    const vars = {};
    vars.application_uuid = this.application_uuid;
    vars.current_step = 3;

    O.PREP([await O.mPOST('wills/upd-app-will-details', vars)])
      .then(async () => {
        await this.goToStep3();
      })
      .catch(() => {});
  },
  async buildSignedWillTable(data, tableDiv) {
    const rows = Array.isArray(data) ? data : [];
    const table = U.id(tableDiv);
    table.innerHTML = '';

    const showRemoveColumn = !this.isViewOnly() && rows.some((row) => row.remove_yn === true);

    // HEADER
    const thead = U.divAtt('thead', 'class', 'w-full');
    const headRow = thead.insertRow();

    const h0 = headRow.insertCell(0);
    h0.innerHTML = 'Import YN';
    const h1 = headRow.insertCell(1);
    h1.innerHTML = 'Date';
    const h2 = headRow.insertCell(2);
    h2.innerHTML = 'Active YN';
    const h3 = headRow.insertCell(3);
    h3.innerHTML = 'Recorded By';
    const h4 = headRow.insertCell(4);
    h4.innerHTML = 'Date Will Recieved';
    const h5 = headRow.insertCell(5);
    h5.innerHTML = 'Date Will Signed';
    const h6 = headRow.insertCell(6);
    h6.innerHTML = 'Verification Date';
    const h7 = headRow.insertCell(7);
    h7.innerHTML = 'Filing Number';
    const h8 = headRow.insertCell(8);
    h8.innerHTML = 'Document Version';
    const h9 = headRow.insertCell(9);
    h9.innerHTML = '';
    if (showRemoveColumn) {
      const h10 = headRow.insertCell(10);
      h10.innerHTML = '';
    }
    thead.appendChild(headRow);
    table.appendChild(thead);

    // Body
    const tbody = U.divAtt('tbody');
    table.appendChild(tbody);

    for (const row of rows) {
      const willRow = tbody.insertRow();
      const r0 = willRow.insertCell(0);
      r0.innerHTML = row.import_dates_yn ? 'Yes' : 'No';
      const r1 = willRow.insertCell(1);
      r1.textContent = formatIsoDateUtc(row.created_date);
      const r2 = willRow.insertCell(2);
      r2.innerHTML = row.active_yn ? 'Yes' : 'No';
      const r3 = willRow.insertCell(3);
      r3.innerHTML = `${row.f_name ?? ''} ${row.s_name ?? ''}`.trim();
      const r4 = willRow.insertCell(4);
      r4.textContent = formatIsoDateUtc(row.received_date);
      const r5 = willRow.insertCell(5);
      r5.textContent = formatIsoDateUtc(row.signed_date);
      const verificationDateVal = row.verification_date ?? row.verificationDate;
      const filingNumVal = row.filing_number ?? row.filingNumber;
      const r6 = willRow.insertCell(6);
      r6.textContent = formatIsoDateUtc(verificationDateVal);
      const r7 = willRow.insertCell(7);
      r7.textContent = filingNumVal != null && filingNumVal !== '' ? String(filingNumVal) : '';
      const r8 = willRow.insertCell(8);
      const docVer = row.document_version_number ?? row.documentVersionNumber;
      r8.textContent =
        docVer != null && docVer !== ''
          ? String(docVer)
          : row.file_reference != null && row.file_reference !== ''
            ? String(row.file_reference)
            : '';
      const r9 = willRow.insertCell(9);
      if (!row.import_dates_yn) {
        const download_onclick = `thsTab.downloadFile('${row.file_uuid}')`;
        const linkSVG = svg.download(download_onclick);
        r9.appendChild(linkSVG);
      }
      if (showRemoveColumn) {
        const r10 = willRow.insertCell(10);
        if (row.remove_yn === true) {
          const replace_onclick = `thsTab.replaceSignedWillModal('${row.file_uuid}')`;
          const replaceSVG = svg.replaceRecycle(replace_onclick);
          r10.appendChild(replaceSVG);
        }
      }

      const progressSteps = document.querySelectorAll('.progressActive');

      progressSteps.forEach((step) => {
        step.classList.add('progressSuccess'); // Add 'progressSuccess' class
        step.classList.remove('progressActive'); // Remove 'progressActive' class if it exists
      });
      thead.classList.add('bg-success');
    }
    if (tableDiv === 'signedWillTableDiv') {
      if (rows.length) {
        U.unhide('signedWillDisplayStep3');
      } else {
        U.hide('signedWillDisplayStep3');
      }
      if (this.isViewOnly() && !this.isFinalisedStatus()) {
        return;
      }
      const uploadRow = U.id('uploadRowSigned');
      if (!uploadRow) {
        return;
      }
      uploadRow.innerHTML = '';

      const uploadDiv = U.divAtt('div');
      uploadDiv.classList.add('relative', 'w-full', 'flex', 'justify-center', 'items-center', 'transition-colors', 'duration-200', 'py-3', 'px-3', 'cursor-pointer', 'bg-primary-opacity', 'hover:bg-secondary', 'border-none');

      uploadDiv.setAttribute('role', 'button');
      uploadDiv.setAttribute('aria-label', 'Upload files');

      uploadDiv.onclick = () => thsTab.uploadSignedWillModal();

      const textcontainer = U.divAtt('span');
      textcontainer.classList.add('ml-2');
      textcontainer.innerHTML = 'Upload Signed Will';

      const linkSVG = svg.uploadCloud();
      uploadDiv.appendChild(linkSVG);
      uploadDiv.appendChild(textcontainer);

      uploadDiv.style.margin = '0';
      uploadDiv.style.width = '100%';
      uploadDiv.style.boxSizing = 'border-box';

      uploadRow.appendChild(uploadDiv);
    }
    if (tableDiv === 'signedWillTableOnly' && (!this.isViewOnly() || this.isFinalisedStatus())) {
      const uploadRow = U.id('uploadRowSignedOnly');
      uploadRow.innerHTML = '';

      const uploadDiv = U.divAtt('div');
      uploadDiv.classList.add('relative', 'w-full', 'flex', 'justify-center', 'items-center', 'transition-colors', 'duration-200', 'py-3', 'px-3', 'cursor-pointer', 'bg-primary-opacity', 'hover:bg-secondary', 'border-none');

      uploadDiv.setAttribute('role', 'button');
      uploadDiv.setAttribute('aria-label', 'Upload files');

      uploadDiv.onclick = () => thsTab.uploadSignedWillModal();

      const textcontainer = U.divAtt('span');
      textcontainer.classList.add('ml-2');
      textcontainer.innerHTML = 'Upload Signed Will';

      const linkSVG = svg.uploadCloud();
      uploadDiv.appendChild(linkSVG);
      uploadDiv.appendChild(textcontainer);

      uploadDiv.style.margin = '0';
      uploadDiv.style.width = '100%';
      uploadDiv.style.boxSizing = 'border-box';

      uploadRow.appendChild(uploadDiv);
    }
    if (tableDiv === 'signedWillTableDiv2') {
      if (rows.length) {
        U.unhide('signedWillDisplayStep4');
      } else {
        U.hide('signedWillDisplayStep4');
      }
    }
  },
  async finaliseWill() {

    U.id('final-modal-btn').disabled = true;
    U.loaderBtn(1, 'final-modal-btn');

    U.id('pdfUpload').classList.remove('input-error');
    U.id('dateReceived').classList.remove('input-error');
    U.id('dateSigned').classList.remove('input-error');
    U.id('verificationDate').classList.remove('input-error');
    U.id('filingNumber').classList.remove('input-error');
    U.id('docVersion').classList.remove('input-error');
    U.id('confirmationCheckbox').classList.remove('input-error');

    // Collect the input values
    const pdfFile = U.id('pdfUpload').files[0];
    const dateReceived = U.id('dateReceived').value;
    const dateSigned = U.id('dateSigned').value;
    const verificationDate = U.id('verificationDate').value;
    const filingNumber = U.id('filingNumber').value;
    const docVersion = U.id('docVersion').value;
    const confirmationCheckbox = U.id('confirmationCheckbox').checked;
    const signingNotes = U.id('signingNotes').value;

    const application_uuid = this.application_uuid;

    let isValid = true;

    if (!pdfFile) {
      U.id('pdfUpload').classList.add('input-error');
      isValid = false;
    }

    if (pdfFile && pdfFile.type !== 'application/pdf') {
      U.id('pdfUpload').classList.add('input-error');
      isValid = false;
    }

    const datesAreReadOnly = U.id('dateReceived').type === 'hidden';
    if (!datesAreReadOnly) {
      if (!dateReceived) {
        U.id('dateReceived').classList.add('input-error');
        isValid = false;
      }

      if (!dateSigned) {
        U.id('dateSigned').classList.add('input-error');
        isValid = false;
      }

      if (!verificationDate) {
        U.id('verificationDate').classList.add('input-error');
        isValid = false;
      }
    }

    if (!filingNumber) {
      U.id('filingNumber').classList.add('input-error');
      isValid = false;
    }

    if (!docVersion) {
      U.id('docVersion').classList.add('input-error');
      isValid = false;
    }

    if (!confirmationCheckbox) {
      U.id('confirmationCheckbox').classList.add('input-error');
      isValid = false;
    }

    if (!isValid) {
      U.loaderBtn(0, 'final-modal-btn');
      U.id('final-modal-btn').disabled = false;
      return;
    }

    // Create a FormData object to send the files and other fields
    const formData = new FormData();
    formData.append('uploadFile', pdfFile);
    formData.append('application_uuid', application_uuid);
    formData.append('received_date', dateReceived);
    formData.append('signed_date', dateSigned);
    formData.append('verification_date', verificationDate);
    formData.append('filing_number', filingNumber);
    formData.append('document_version_uuid', docVersion);
    formData.append('author_signature', confirmationCheckbox);
    formData.append('notes', signingNotes);

    const hdrs = await O.getAuthHdrs();

    // Send the form data using fetch
    try {
      const response = await fetch(`${config.apiURL}wills/upl-signed-will`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${hdrs.token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        // Try to parse error response as JSON, fallback to text
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          const errorText = await response.text();
          errorData = { status: 'FAILED', data: { error: errorText || 'File upload failed' } };
        }
        
        // Structure error for handleFetchError
        throw {
          status: response.status,
          data: errorData
        };
      }

      const result = await response.json();
      U.loaderBtn(0, 'final-modal-btn');
      U.closeModal('fileUploadModal');
      await this.goToStep4();
    } catch (error) {
      U.loaderBtn(0, 'final-modal-btn');
      U.id('final-modal-btn').disabled = false;
      
      // Check if error is already structured for handleFetchError
      if (error.status && error.data) {
        O.handleFetchError(error);
      } else {
        // Fallback for other errors (network errors, etc.)
        const errorMsg = error.message || 'Failed to upload file. Please try again.';
        comp.errorBox('Error', errorMsg);
      }
    }
  },
  async requestRedraft() {
    U.id('reasonNotes').classList.remove('input-error');

    const vars = {};
    vars.application_uuid = this.application_uuid;
    vars.redraft_reason = U.id('reasonNotes').value;

    if (!vars.redraft_reason) {
      U.id('reasonNotes').classList.add('input-error');
      return;
    }

    O.PREP([await O.mPOST('wills/req-will-redraft', vars)])
      .then(async () => {
        U.closeModal('redraftModal');
        this.render();
      })
      .catch(() => {});
  },
  async requestRedraftModal() {
    const popup = await this.redraftModal();
    document.body.prepend(popup);
  },
  async redraftModal() {
    const div = `
		  <div id="redraftModal" class="fixed inset-0 z-50 overflow-y-auto bg-opacity-50 bg-black">
			<div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
			  <div class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
				<!-- Modal Content -->
				<div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
				  <div class="text-center">
					<!-- Heading -->
					<h2 class="text-2xl font-semibold mb-4">Request For Redraft</h2>
				  </div>
	  
				  <!-- Reason/Notes Text Area -->
				  <div class="text-left">
					<label for="reasonNotes" class="block text-gray-700 font-medium">Reason/Notes:</label>
					<textarea id="reasonNotes" placeholder="Reason/Notes" class="block w-4/5 mx-auto px-4 py-2 mt-2 bg-white border border-gray-300 rounded-lg resize-none focus:outline-none"></textarea>
				  </div>
				</div>
	  
				<!-- Footer Buttons -->
				<div class="bg-gray-50 px-4 py-3 flex md:justify-end justify-center sm:px-6">
				  <button class="btn-outline mr-3" onclick="U.closeModal('redraftModal')">Cancel</button>
				  <button id="modal-btn" class="btn-primary" onclick="thsTab.requestRedraft()">Request Redraft</button>
				</div>
			  </div>
			</div>
		  </div>
		`;

    return await U.createHTMLElementFromString(div);
  },
};
